import {
  PROFESSIONAL_STATUS_BADGE,
  ACCESS_LEVEL_BADGE,
  formatDate,
} from './helpers';
import { FiCheck, FiX } from 'react-icons/fi';
import { TbArrowDown } from 'react-icons/tb';
import Button from '../common/Button';
import Icon from '../common/Icon';

/**
 * AssignmentCard — one corporate→professional delegation, rendered as a card
 * for the unified dashboard list. Tagged "Delegated" to distinguish it from
 * access requests the professional initiated.
 */
export default function AssignmentCard({ assignment, onAccept, onReject, onViewDetails }) {
  const status = PROFESSIONAL_STATUS_BADGE[assignment.professionalStatus] || PROFESSIONAL_STATUS_BADGE.pending;
  const access = ACCESS_LEVEL_BADGE[assignment.accessLevel] || ACCESS_LEVEL_BADGE.read;
  const isPending = assignment.professionalStatus === 'pending';
  const by = assignment.assignedBy || {};
  const companies = (assignment.assignedCompanies || [])
    .map((ac) => ac.company)
    .filter(Boolean);

  return (
    <div
      className="bg-white rounded-[12px] border p-4 transition-shadow hover:shadow-md"
      style={{ borderColor: '#ddd6c6' }}
    >
      {/* Header: source tag + status */}
      <div className="flex items-start justify-between gap-3">
        <span
          className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.5px] px-2 py-[3px] rounded"
          style={{ background: '#145f5f14', color: '#145f5f' }}
        >
          <TbArrowDown size={10} aria-hidden="true" /> Delegated to you
        </span>
        <span
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold"
          style={{ background: status.bg, color: status.color }}
        >
          <Icon name={status.icon} size={11} /> {status.label}
        </span>
      </div>

      {/* Client */}
      <div className="mt-2">
        <div className="text-[14px] font-bold leading-tight" style={{ color: '#08111f' }}>
          {by.fullName || 'Unknown client'}
        </div>
        <div className="text-[11px] mt-0.5 truncate" style={{ color: '#7a7060' }}>
          {by.email || 'No email'}
          {by.companyName ? ` · ${by.companyName}` : ''}
        </div>
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t" style={{ borderColor: '#f0ebdf' }}>
        <div>
          <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
            Access level
          </div>
          <div className="mt-1">
            <span
              className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[10.5px] font-semibold"
              style={{ background: access.bg, color: access.color }}
            >
              <Icon name={access.icon} size={11} /> {access.label}
            </span>
          </div>
        </div>
        <div>
          <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
            Scope
          </div>
          <div className="text-[11.5px] font-semibold mt-1.5 inline-flex items-center gap-1" style={{ color: '#08111f' }}>
            {assignment.assignmentScope === 'all_companies' ? (
              <><Icon name="global" size={12} /> All companies</>
            ) : (
              <><Icon name="🏢" size={12} /> {companies.length} {companies.length === 1 ? 'company' : 'companies'}</>
            )}
          </div>
        </div>
        <div>
          <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
            Delegated on
          </div>
          <div className="text-[11.5px] font-semibold mt-1.5" style={{ color: '#08111f' }}>
            {formatDate(assignment.createdAt)}
          </div>
        </div>
      </div>

      {/* Company chips (specific scope) */}
      {assignment.assignmentScope === 'specific_companies' && companies.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {companies.slice(0, 4).map((c, i) => (
            <span
              key={c._id || i}
              className="px-2 py-[3px] rounded text-[10.5px]"
              style={{ background: '#edeef3', color: '#3a4a66' }}
            >
              {c.name}
            </span>
          ))}
          {companies.length > 4 && (
            <span
              className="px-2 py-[3px] rounded text-[10.5px]"
              style={{ background: '#f0ece2', color: '#7a7060' }}
            >
              +{companies.length - 4} more
            </span>
          )}
        </div>
      )}

      {/* Note from the client */}
      {assignment.notes && (
        <div className="text-[11px] mt-2.5 italic leading-relaxed" style={{ color: '#7a7060' }}>
          “{assignment.notes}”
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: '#f0ebdf' }}>
        {isPending ? (
          <>
            <Button variant="success" size="sm" onClick={() => onAccept(assignment)} leftIcon={<FiCheck size={15} />}>
              Accept
            </Button>
            <Button variant="danger" size="sm" onClick={() => onReject(assignment)} leftIcon={<FiX size={15} />}>
              Reject
            </Button>
            <button
              type="button"
              onClick={() => onViewDetails(assignment)}
              className="ml-auto text-[11.5px] font-semibold transition-opacity hover:opacity-70"
              style={{ color: '#145f5f' }}
            >
              View details →
            </button>
          </>
        ) : (
          <>
            <span className="text-[11px]" style={{ color: '#a39a88' }}>
              {assignment.professionalStatus === 'accepted'
                ? `Accepted${assignment.professionalResponseAt ? ` · ${formatDate(assignment.professionalResponseAt)}` : ''}`
                : `Declined${assignment.professionalResponseAt ? ` · ${formatDate(assignment.professionalResponseAt)}` : ''}`}
            </span>
            <button
              type="button"
              onClick={() => onViewDetails(assignment)}
              className="ml-auto text-[11.5px] font-semibold transition-opacity hover:opacity-70"
              style={{ color: '#145f5f' }}
            >
              View details →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
